import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Textarea,
  Button,
  Input,
  useToast,
  Spinner,
  IconButton,
  Badge,
  Divider,
  List,
  ListItem,
  ListIcon,
  ButtonGroup,
} from '@chakra-ui/react';
import { ArrowBackIcon, CopyIcon, CheckIcon, LinkIcon, EditIcon } from '@chakra-ui/icons';

const apiUrl = process.env.REACT_APP_API_URL || '/api';

function MeetingNotes() {
  const navigate = useNavigate();
  const toast = useToast();
  const [inputMode, setInputMode] = useState('paste'); // 'paste' or 'url'
  const [transcript, setTranscript] = useState('');
  const [clickupUrl, setClickupUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [results, setResults] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);

  const handleFetchClickUp = async () => {
    if (!clickupUrl.trim()) {
      toast({
        title: 'Please enter a ClickUp URL',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setFetchingUrl(true);

    try {
      const response = await fetch(`${apiUrl}/fetch-clickup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: clickupUrl }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch ClickUp document');
      }

      setTranscript(data.content);
      setInputMode('paste'); // Switch to paste mode to show the content
      toast({
        title: 'Transcript loaded',
        description: 'ClickUp document content has been loaded. You can review and edit before parsing.',
        status: 'success',
        duration: 4000,
      });
    } catch (error) {
      toast({
        title: 'Error fetching ClickUp document',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setFetchingUrl(false);
    }
  };

  const handleSubmit = async () => {
    if (!transcript.trim()) {
      toast({
        title: 'Please enter a meeting transcript',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      const response = await fetch(`${apiUrl}/meeting-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ transcript }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to parse meeting notes');
      }

      setResults(data.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const copySection = (section, title, items) => {
    const text = items.join('\n');
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  const copyAll = () => {
    if (!results) return;

    const sections = [
      { title: 'DESIGN TEAM', items: results.design },
      { title: 'CONTENT TEAM', items: results.content },
      { title: 'DEV TEAM', items: results.dev },
      { title: 'CLIENT FOLLOW-UPS', items: results.clientFollowups },
    ];

    const text = sections
      .map(
        (s) =>
          `${s.title}\n${s.items.length > 0 ? s.items.join('\n') : 'No items'}`
      )
      .join('\n\n');

    navigator.clipboard.writeText(text);
    toast({
      title: 'All notes copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  const TeamSection = ({ title, items, color, section }) => (
    <Box
      bg="#1A1F2E"
      p={6}
      borderRadius="xl"
      borderWidth="1px"
      borderColor="gray.700"
      width="100%"
    >
      <HStack justify="space-between" mb={4}>
        <HStack>
          <Badge colorScheme={color} fontSize="sm" px={3} py={1} borderRadius="md">
            {title}
          </Badge>
          <Text color="gray.500" fontSize="sm">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </Text>
        </HStack>
        <IconButton
          icon={copiedSection === section ? <CheckIcon /> : <CopyIcon />}
          size="sm"
          variant="ghost"
          colorScheme={copiedSection === section ? 'green' : 'gray'}
          onClick={() => copySection(section, title, items)}
          aria-label="Copy section"
          isDisabled={items.length === 0}
        />
      </HStack>
      {items.length > 0 ? (
        <List spacing={3}>
          {items.map((item, index) => (
            <ListItem key={index} color="gray.300" fontSize="sm" display="flex">
              <ListIcon as={() => <Text as="span" mr={2}>•</Text>} />
              <Text>{item}</Text>
            </ListItem>
          ))}
        </List>
      ) : (
        <Text color="gray.500" fontSize="sm" fontStyle="italic">
          No items for this team
        </Text>
      )}
    </Box>
  );

  return (
    <VStack spacing={8} align="stretch" maxW="900px" mx="auto" pt={8} px={4} pb={12}>
      {/* Header */}
      <HStack justify="space-between" align="center">
        <HStack spacing={4}>
          <IconButton
            icon={<ArrowBackIcon />}
            variant="ghost"
            onClick={() => navigate('/')}
            aria-label="Back to home"
            color="gray.400"
            _hover={{ color: 'white', bg: 'whiteAlpha.100' }}
          />
          <Box>
            <Heading size="lg" color="white">
              Meeting Notes Parser
            </Heading>
            <Text color="gray.400" fontSize="sm">
              Organize meeting notes into action items by team
            </Text>
          </Box>
        </HStack>
      </HStack>

      {/* Input Section */}
      <Box
        bg="#1A1F2E"
        p={6}
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.700"
      >
        <VStack spacing={4} align="stretch">
          {/* Input Mode Toggle */}
          <VStack align="stretch" spacing={2}>
            <Text color="gray.300" fontWeight="medium">
              How would you like to add the transcript?
            </Text>
            <ButtonGroup size="sm" isAttached variant="outline" width="fit-content">
              <Button
                leftIcon={<EditIcon />}
                onClick={() => setInputMode('paste')}
                bg={inputMode === 'paste' ? 'purple.600' : 'transparent'}
                color={inputMode === 'paste' ? 'white' : 'gray.400'}
                borderColor={inputMode === 'paste' ? 'purple.600' : 'gray.600'}
                _hover={{
                  bg: inputMode === 'paste' ? 'purple.600' : 'whiteAlpha.100',
                }}
              >
                Paste Text
              </Button>
              <Button
                leftIcon={<LinkIcon />}
                onClick={() => setInputMode('url')}
                bg={inputMode === 'url' ? 'purple.600' : 'transparent'}
                color={inputMode === 'url' ? 'white' : 'gray.400'}
                borderColor={inputMode === 'url' ? 'purple.600' : 'gray.600'}
                _hover={{
                  bg: inputMode === 'url' ? 'purple.600' : 'whiteAlpha.100',
                }}
              >
                Fetch from ClickUp
              </Button>
            </ButtonGroup>
            <Text color="gray.500" fontSize="xs">
              {inputMode === 'paste'
                ? 'Copy and paste your meeting notes or transcript directly'
                : 'Enter the URL of a ClickUp Doc containing the meeting notes'}
            </Text>
          </VStack>

          {/* URL Input Mode */}
          {inputMode === 'url' && (
            <HStack spacing={3}>
              <Input
                value={clickupUrl}
                onChange={(e) => setClickupUrl(e.target.value)}
                placeholder="https://app.clickup.com/..."
                bg="#0F1419"
                borderColor="gray.600"
                _hover={{ borderColor: 'gray.500' }}
                _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px #7c3aed' }}
                color="white"
                fontSize="sm"
              />
              <Button
                onClick={handleFetchClickUp}
                isLoading={fetchingUrl}
                loadingText="Fetching..."
                colorScheme="purple"
                minW="120px"
              >
                Fetch
              </Button>
            </HStack>
          )}

          {/* Transcript Textarea - always visible */}
          <Textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            placeholder={
              inputMode === 'url'
                ? 'Transcript will appear here after fetching from ClickUp...'
                : 'Paste your meeting transcript or notes here...'
            }
            size="lg"
            minH="200px"
            bg="#0F1419"
            borderColor="gray.600"
            _hover={{ borderColor: 'gray.500' }}
            _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px #7c3aed' }}
            color="white"
            fontSize="sm"
          />

          <Button
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Parsing..."
            bg="linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)"
            color="white"
            size="lg"
            _hover={{
              bg: 'linear-gradient(135deg, #6b2dc9 0%, #d63384 100%)',
              transform: 'translateY(-1px)',
            }}
            _active={{ transform: 'translateY(0)' }}
            isDisabled={!transcript.trim()}
          >
            Parse Meeting Notes
          </Button>
        </VStack>
      </Box>

      {/* Loading State */}
      {loading && (
        <VStack py={12}>
          <Spinner size="xl" color="purple.500" thickness="3px" />
          <Text color="gray.400">Analyzing transcript...</Text>
        </VStack>
      )}

      {/* Results */}
      {results && !loading && (
        <VStack spacing={6} align="stretch">
          {/* Summary */}
          {results.summary && (
            <Box
              bg="#1A1F2E"
              p={6}
              borderRadius="xl"
              borderWidth="1px"
              borderColor="gray.700"
            >
              <Text color="gray.400" fontSize="sm" fontWeight="medium" mb={2}>
                MEETING SUMMARY
              </Text>
              <Text color="white">{results.summary}</Text>
            </Box>
          )}

          {/* Copy All Button */}
          <HStack justify="flex-end">
            <Button
              leftIcon={<CopyIcon />}
              variant="outline"
              size="sm"
              colorScheme="purple"
              onClick={copyAll}
            >
              Copy All Notes
            </Button>
          </HStack>

          <Divider borderColor="gray.700" />

          {/* Team Sections */}
          <TeamSection
            title="DESIGN TEAM"
            items={results.design}
            color="pink"
            section="design"
          />
          <TeamSection
            title="CONTENT TEAM"
            items={results.content}
            color="orange"
            section="content"
          />
          <TeamSection
            title="DEV TEAM"
            items={results.dev}
            color="blue"
            section="dev"
          />
          <TeamSection
            title="CLIENT FOLLOW-UPS"
            items={results.clientFollowups}
            color="green"
            section="client"
          />
        </VStack>
      )}
    </VStack>
  );
}

export default MeetingNotes;
